import { Prisma } from '@prisma/client';
import {
  QboAccount,
  QboBill,
  QboBillPayment,
  QboCustomer,
  QboInvoice,
  QboItem,
  QboPayment,
  QboRef,
  QboVendor,
} from '../quickbooks.types';

export type QboAccountMetadata = {
  id: string;
  name: string;
  accountNumber: string;
  syncToken: string;
  classification: 'Asset' | 'Equity' | 'Expense' | 'Liability' | 'Revenue';
};

export type QboCustomerMetadata = { id: string; name: string };

export type QboVendorMetadata = { id: string; name: string };

export type QboItemMetadata = { id: string; name: string; expenseAccountRef: QboRef; incomeAccountRef: QboRef };

export type QboInvoiceMetadata = { id: string; syncToken: string };

export type QboBillMetadata = { id: string; syncToken: string };

export type QboBillPaymentMetadata = { id: string; syncToken: string; txnDate: string; totalAmt: number };

export type QboPaymentMetadata = { id: string; syncToken: string; txnDate: string; totalAmt: number };

/**
 * Either direction of a payment link (AR Payment or AP BillPayment).
 * The on-disk shape is identical, so a single parser works for both.
 */
export type QboPaymentLinkMetadata = QboBillPaymentMetadata | QboPaymentMetadata;

// Metadata serialization changed over time, so a stored value may be a JSON string we must parse or an object Prisma already parsed.
const asMetaData = (metaDataAsAnyType: Prisma.JsonValue) => {
  if (typeof metaDataAsAnyType === 'string') {
    return JSON.parse(metaDataAsAnyType);
  } else {
    return metaDataAsAnyType;
  }
};
export const parseQboAccountMetadata = (metadata: Prisma.JsonValue): QboAccountMetadata => asMetaData(metadata);
export const parseQboCustomerMetadata = (metadata: Prisma.JsonValue): QboCustomerMetadata => asMetaData(metadata);
export const parseQboVendorMetadata = (metadata: Prisma.JsonValue): QboVendorMetadata => asMetaData(metadata);
export const parseQboItemMetadata = (metadata: Prisma.JsonValue): QboItemMetadata => asMetaData(metadata);
export const parseQboInvoiceMetadata = (metadata: Prisma.JsonValue): QboInvoiceMetadata => asMetaData(metadata);
export const parseQboBillMetadata = (metadata: Prisma.JsonValue): QboBillMetadata => asMetaData(metadata);
export const parseQboBillPaymentMetadata = (metadata: Prisma.JsonValue): QboBillPaymentMetadata => asMetaData(metadata);
export const parseQboPaymentMetadata = (metadata: Prisma.JsonValue): QboPaymentMetadata => asMetaData(metadata);
export const parseQboPaymentLinkMetadata = (metadata: Prisma.JsonValue): QboPaymentLinkMetadata => asMetaData(metadata);

export const selectQboAccountMetaData = (account: QboAccount): QboAccountMetadata => ({
  id: account.Id,
  name: account.Name,
  accountNumber: account.AcctNum,
  classification: account.Classification,
  syncToken: account.SyncToken,
});

export const selectQboCustomerMetaData = (account: QboCustomer): QboCustomerMetadata => ({
  id: account.Id,
  name: account.DisplayName,
});

export const selectQboVendorMetaData = (account: QboVendor): QboVendorMetadata => ({
  id: account.Id,
  name: account.DisplayName,
});

export const selectQboItemMetadata = (item: QboItem): QboItemMetadata => ({
  id: item.Id,
  name: item.Name,
  incomeAccountRef: item.IncomeAccountRef,
  expenseAccountRef: item.ExpenseAccountRef,
});

export const selectQboInvoiceMetadata = (invoice: QboInvoice): QboInvoiceMetadata => ({
  id: invoice.Id,
  syncToken: invoice.SyncToken,
});

export const selectQboBillMetadata = (bill: QboBill): QboBillMetadata => ({ id: bill.Id, syncToken: bill.SyncToken });

export const selectQboBillPaymentMetadata = (billPayment: QboBillPayment): QboBillPaymentMetadata => ({
  id: billPayment.Id,
  syncToken: billPayment.SyncToken,
  txnDate: billPayment.TxnDate,
  totalAmt: billPayment.TotalAmt,
});

export const selectQboPaymentMetadata = (payment: QboPayment): QboPaymentMetadata => ({
  id: payment.Id,
  syncToken: payment.SyncToken,
  txnDate: payment.TxnDate,
  totalAmt: payment.TotalAmt,
});
